import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Stethoscope,
  CheckCircle
} from 'lucide-react';

const ForClinics = () => {
  const services = [
    {
      category: "AMHAS Urgent Care Facilities",
      coverage: "100%",
      description: "Vertically integrated urgent care centers providing comprehensive services",
      items: [
        "Urgent care & clinical treatment services",
        "Laboratory services and blood work",
        "Radiology and diagnostic imaging",
        "Essential pharmaceuticals and medications",
        "Observation beds for extended treatment",
        "Non-surgical emergency care"
      ]
    },
    {
      category: "AI + Telehealth Services",
      coverage: "100%",
      description: "School-based AI and telehealth services",
      items: [
        "AI-powered diagnosis and care planning",
        "Western board-certified doctor consultations",
        "Remote nurse support and vitals monitoring",
        "Care coordination and referrals",
        "Follow-up care and monitoring"
      ]
    },
    {
      category: "Preventive Care",
      coverage: "100%",
      description: "Comprehensive preventive services",
      items: [
        "Annual health checkups",
        "Vaccination programs",
        "Health education sessions",
        "Growth monitoring",
        "Nutritional assessments"
      ]
    }
  ];


  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="section-padding bg-gradient-to-br from-primary-600 to-accent-600 text-white">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center max-w-4xl mx-auto"
          >
            <h1 className="text-5xl md:text-6xl font-bold mb-6">AMHAS Urgent Care Facilities</h1>
            <p className="text-xl md:text-2xl text-primary-100">
              AMHAS operates through vertically integrated urgent care facilities providing 100% coverage for all services. 
              Care is also delivered via AI + Telehealth at schools. As we scale, urgent care centers are being established 
              while AI and telehealth services expand to all schools.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Services Covered */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="section-title">Services & Coverage</h2>
            <p className="section-subtitle">
              100% coverage at AMHAS Urgent Care Facilities for all services
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {services.map((service, index) => (
              <motion.div
                key={service.category}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="card p-8"
              >
                <div className="text-center mb-6">
                  <div className="text-3xl font-bold text-primary-600 mb-2">{service.coverage}</div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">{service.category}</h3>
                  <p className="text-gray-600">{service.description}</p>
                </div>

                <ul className="space-y-3">
                  {service.items.map((item, itemIndex) => (
                    <li key={itemIndex} className="flex items-start space-x-3">
                      <CheckCircle size={16} className="text-accent-600 mt-1 flex-shrink-0" />
                      <span className="text-gray-700 text-sm">{item}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding bg-primary-600 text-white">
        <div className="container-custom text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl font-bold mb-4">AMHAS Urgent Care Facilities</h2>
            <p className="text-xl text-primary-100 mb-8 max-w-3xl mx-auto">
              AMHAS operates through vertically integrated urgent care facilities providing world-class healthcare. 
              All care is delivered through our own facilities - we do not use external provider networks.
            </p>

            <div className="flex flex-col sm:flex-row items-center justify-center space-y-4 sm:space-y-0 sm:space-x-6">
              <Link to="/urgent-care" className="btn-secondary">
                View Urgent Care Centers
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default ForClinics;
